import { useState, useEffect, useCallback } from 'react'
import { Truck, RefreshCw, Loader2, Route, Plus, Pencil, Trash2, X, ArrowRight, Package, Layers, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { equipmentOf } from '../equipment'
import { useConfirm } from './ConfirmDialog'
import { LoadingState, ErrorState } from './ViewState'
import { apiError } from '../lib/api'

const EMPTY = { load_zone: '', dump_zone: '', material: '', route: '' }
const NO_ROUTE = '__no_route__'

export default function DispatchView() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { devices, categories, mines } = useWS()
  const { matchesScope } = useScope()
  const [assignments, setAssignments] = useState([])
  const [zones, setZones] = useState([])
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [confirmDialog, ask] = useConfirm()

  // loadError = falha de CARGA (a tela não pode ser confiada); error = falha de uma ação
  // do drawer. Antes tudo caía no mesmo lugar, dentro do formulário, visível só com o
  // drawer aberto: com /api/zones em 500 a tela pedia para cadastrar zonas existentes.
  const [loadError, setLoadError] = useState(null)

  const loadAssignments = useCallback(async () => {
    try {
      const res = await fetch('/api/assignments')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setAssignments(await res.json() || [])
      setLoadError(null)
    } catch (err) { setLoadError(err.message) }
  }, [])

  const loadAll = useCallback(() => {
    const ctrl = new AbortController()
    setLoading(true)
    const get = (url) => fetch(url, { signal: ctrl.signal }).then(r => {
      if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`)
      return r.json()
    })
    Promise.all([get('/api/assignments'), get('/api/zones'), get('/api/articles')])
      .then(([a, z, ar]) => {
        setAssignments(a || [])
        setZones(z || [])
        setArticles(ar || [])
        setLoadError(null)
      })
      .catch(err => { if (err.name !== 'AbortError') setLoadError(err.message) })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [])

  useEffect(() => loadAll(), [loadAll])

  const loadZones = zones.filter(z => z.type === 'load')
  const dumpZones = zones.filter(z => z.type === 'dump')
  const byName = (name) => devices.find(d => d.name === name)

  // /api/assignments não aceita ?mine=: o recorte é feito aqui, pelos devices no escopo.
  const scopedDevices = devices.filter(matchesScope)
  const scopedNames = new Set(scopedDevices.map(d => d.name))
  const scopedAssignments = assignments.filter(a => scopedNames.has(a.device_name))

  const allocated = new Set(scopedAssignments.map(a => a.device_name))
  const unallocated = scopedDevices.filter(d => !allocated.has(d.name))

  const lanes = (() => {
    const map = new Map()
    for (const a of scopedAssignments) {
      const key = a.route && a.route.trim() ? a.route.trim() : NO_ROUTE
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(a)
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === NO_ROUTE) return 1
      if (b[0] === NO_ROUTE) return -1
      return a[0].localeCompare(b[0])
    })
  })()

  const openCreate = (device) => {
    setTarget(device); setEditingId(null); setForm(EMPTY); setError(null); setOpen(true)
  }
  const openEdit = (assignment) => {
    setTarget(byName(assignment.device_name) || { name: assignment.device_name })
    setEditingId(assignment.id)
    setForm({
      load_zone: assignment.load_zone || '',
      dump_zone: assignment.dump_zone || '',
      material: assignment.material || '',
      route: assignment.route || '',
    })
    setError(null); setOpen(true)
  }
  const close = () => { setOpen(false); setTarget(null); setEditingId(null) }

  const confirm = async (e) => {
    e.preventDefault()
    if (!target) return
    setSaving(true); setError(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(target.name)}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw await apiError(res)
      await loadAssignments()
      close()
    } catch (err) { setError(err.message) } finally { setSaving(false) }
  }

  const remove = (a) => ask({
    title: t('production:dispatch.remove'),
    message: t('production:dispatch.remove_confirm', { name: byName(a.device_name) ? equipmentOf(byName(a.device_name), categories, mines).name || a.device_name : a.device_name }),
    confirmLabel: t('production:dispatch.remove'),
    danger: true,
    onConfirm: async () => {
      const res = await fetch(`/api/assignments/${a.id}`, { method: 'DELETE' })
      if (!res.ok) throw await apiError(res)
      await loadAssignments()
    },
  })

  const noZones = !loadError && zones.length === 0

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Truck size={18} className="text-brand" />
          <span className="font-semibold text-sm">{t('production:dispatch.title')}</span>
        </div>
        <div className="flex items-center gap-4 ml-2 text-sm">
          <span className="text-slate-500 dark:text-slate-400">{t('production:dispatch.allocated')} <b className="text-status-op tabular-nums">{allocated.size}</b></span>
          <span className="text-slate-500 dark:text-slate-400">{t('production:dispatch.unallocated')} <b className="text-slate-700 dark:text-slate-200 tabular-nums">{unallocated.length}</b></span>
        </div>
        <button onClick={loadAssignments} title={t('common:actions.refresh')}
          className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-brand-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
          <RefreshCw size={14} /> {t('common:actions.refresh')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        {loading ? (
          <LoadingState label={t('production:dispatch.loading')} className="h-full" />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={loadAll} className="h-full" />
        ) : (
          <div className="space-y-5">
            {noZones && (
              <div className="flex items-center gap-2 text-sm text-status-stop bg-status-stop/10 border border-status-stop/20 rounded-lg px-4 py-3">
                <AlertTriangle size={16} />
                {t('production:dispatch.no_zones_warning')}
              </div>
            )}

            {lanes.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500">
                <Route size={28} />
                <span className="text-sm">{t('production:dispatch.empty')}</span>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {lanes.map(([route, items]) => (
                  <div key={route} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                      <Route size={15} className="text-brand shrink-0" />
                      <span className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">{route === NO_ROUTE ? t('production:dispatch.no_route') : route}</span>
                      <span className="ml-auto text-xs text-slate-400 dark:text-slate-500 tabular-nums">{items.length}</span>
                    </div>
                    <div className="p-3 space-y-2">
                      {items.map(a => {
                        const dev = byName(a.device_name)
                        const eq = dev ? equipmentOf(dev, categories, mines) : null
                        return (
                          <div key={a.id} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 p-3">
                            <div className="flex items-center gap-2.5">
                              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shrink-0">
                                <span className="equip-icon block w-5 h-5 text-slate-500 dark:text-slate-400"
                                  style={{ WebkitMaskImage: `url(/equipments/${eq?.svg || 'wheel_tractor'}.svg)`, maskImage: `url(/equipments/${eq?.svg || 'wheel_tractor'}.svg)` }} />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">{eq?.name || a.device_name}</p>
                                {eq?.label && <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{eq.label}</p>}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                              <span className="truncate">{a.load_zone || '—'}</span>
                              <ArrowRight size={12} className="text-slate-400 dark:text-slate-500 shrink-0" />
                              <span className="truncate">{a.dump_zone || '—'}</span>
                            </div>
                            {a.material && (
                              <div className="mt-1.5">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-brand/10 text-brand-700 font-medium">
                                  <Package size={11} /> {a.material}
                                </span>
                              </div>
                            )}
                            <div className="mt-2.5 flex items-center gap-1.5">
                              <button onClick={() => openEdit(a)} disabled={!can('engineer')} title={!can('engineer') ? t('admin:perms.needs_engineer') : undefined}
                                className="inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 hover:text-brand-700 px-2 py-1 rounded-md hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                <Pencil size={12} /> {t('common:actions.edit')}
                              </button>
                              <button onClick={() => remove(a)} disabled={!can('engineer')} title={!can('engineer') ? t('admin:perms.needs_engineer') : undefined}
                                className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-status-down px-2 py-1 rounded-md hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                <Trash2 size={12} /> {t('production:dispatch.remove')}
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <Layers size={15} className="text-slate-400 dark:text-slate-500 shrink-0" />
                <span className="font-semibold text-sm text-slate-800 dark:text-slate-100">{t('production:dispatch.unallocated')}</span>
                <span className="ml-auto text-xs text-slate-400 dark:text-slate-500 tabular-nums">{unallocated.length}</span>
              </div>
              <div className="p-3">
                {unallocated.length === 0 ? (
                  <p className="text-sm text-slate-400 dark:text-slate-500 px-1 py-2">{t('production:dispatch.all_allocated')}</p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {unallocated.map(d => {
                      const eq = equipmentOf(d, categories, mines)
                      return (
                        <div key={d.name} className="flex items-center gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 p-2.5">
                          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shrink-0">
                            <span className="equip-icon block w-5 h-5 text-slate-500 dark:text-slate-400"
                              style={{ WebkitMaskImage: `url(/equipments/${eq.svg}.svg)`, maskImage: `url(/equipments/${eq.svg}.svg)` }} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">{eq.name || d.name}</p>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{eq.label}</p>
                          </div>
                          <button onClick={() => openCreate(d)} disabled={noZones || !can('engineer')}
                            title={!can('engineer') ? t('admin:perms.needs_engineer') : undefined}
                            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-white bg-brand hover:bg-brand-700 px-2.5 py-1.5 rounded-md shrink-0 disabled:bg-slate-200 disabled:text-slate-400">
                            <Plus size={12} /> {t('production:dispatch.allocate')}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmDialog}

      <div className={`fixed inset-0 z-40 ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
        <div onClick={close}
          className={`fixed inset-0 bg-slate-900/40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`} />
        <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 dark:text-slate-100">{editingId ? t('production:dispatch.edit_assignment') : t('production:dispatch.allocate_equipment')}</p>
              {target && <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{(byName(target.name) && equipmentOf(byName(target.name), categories, mines).name) || target.name}</p>}
            </div>
            <button onClick={close} className="ml-auto text-slate-400 dark:text-slate-500 hover:text-slate-600"><X size={18} /></button>
          </div>

          <form id="dispatch-form" onSubmit={confirm} className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('production:dispatch.load_zone')}</span>
              <select required value={form.load_zone} onChange={e => setForm({ ...form, load_zone: e.target.value })}
                className="mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-brand/30">
                <option value="">{t('production:dispatch.select_placeholder')}</option>
                {form.load_zone && !loadZones.some(z => z.name === form.load_zone) && <option value={form.load_zone}>{form.load_zone}</option>}
                {loadZones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
              </select>
              {loadZones.length === 0 && <span className="text-[11px] text-slate-400 dark:text-slate-500">{t('production:dispatch.no_load_zones')}</span>}
            </label>

            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('production:dispatch.dump_zone')}</span>
              <select required value={form.dump_zone} onChange={e => setForm({ ...form, dump_zone: e.target.value })}
                className="mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-brand/30">
                <option value="">{t('production:dispatch.select_placeholder')}</option>
                {form.dump_zone && !dumpZones.some(z => z.name === form.dump_zone) && <option value={form.dump_zone}>{form.dump_zone}</option>}
                {dumpZones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
              </select>
              {dumpZones.length === 0 && <span className="text-[11px] text-slate-400 dark:text-slate-500">{t('production:dispatch.no_dump_zones')}</span>}
            </label>

            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('common:entities.material')}</span>
              <select value={form.material} onChange={e => setForm({ ...form, material: e.target.value })}
                className="mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-brand/30">
                <option value="">{t('production:dispatch.select_placeholder')}</option>
                {form.material && !articles.some(a => a.description === form.material) && <option value={form.material}>{form.material}</option>}
                {articles.map(a => <option key={a.id} value={a.description}>{a.description}</option>)}
              </select>
              {articles.length === 0 && <span className="text-[11px] text-slate-400 dark:text-slate-500">{t('production:dispatch.no_materials')}</span>}
            </label>

            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('production:dispatch.route')}</span>
              <input value={form.route} onChange={e => setForm({ ...form, route: e.target.value })} placeholder={t('production:dispatch.route_placeholder')}
                className="mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
            </label>

            {error && <p className="text-xs text-status-down">{error}</p>}
          </form>

          <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <button type="button" onClick={close} className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">{t('common:actions.cancel')}</button>
            <button type="submit" form="dispatch-form" disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('production:dispatch.confirm_allocation')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
